import { CommandProps, mergeAttributes, Node, textblockTypeInputRule } from '@tiptap/core';

export type Level = 1 | 2 | 3 | 4 | 5 | 6;

export interface HeadingOptions {
	levels: Array<{
		level: Level;
		className?: string;
	}>;
	HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
	interface Commands<ReturnType> {
		headings: {
			setHeading: (attributes: { level: Level; className?: string }) => ReturnType;
			toggleHeading: (attributes: { level: Level; className?: string }) => ReturnType;
		};
	}
}

export const Headings = Node.create<HeadingOptions>({
	name: 'headings',
	addOptions() {
		return {
			levels: [
				{
					level: 1,
					className: 'h-1',
				},
				{
					level: 2,
					className: 'h-2',
				},
				{
					level: 3,
					className: 'h-1',
				},
				{
					level: 4,
					className: 'h-4',
				},
				{
					level: 5,
					className: 'h-5',
				},
				{
					level: 6,
					className: 'h-6',
				},
			],
			HTMLAttributes: {},
		};
	},
	content: 'inline*',
	group: 'block',
	defining: true,
	addAttributes() {
		return {
			level: {
				default: {
					level: 1,
					className: 'h-1',
				},
				rendered: false,
			},
		};
	},
	parseHTML() {
		return this.options.levels.map(({ level, className }) => ({
			tag: `h${level}`,
			attrs: {
				level,
				className,
			},
		}));
	},
	renderHTML({ node, HTMLAttributes }) {
		const current = this.options.levels.find(({ level }) => node.attrs.level === level);
		const level = current?.level ?? this.options.levels?.[0]?.level;

		return [
			`h${level}`,
			mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { class: current?.className }),
			0,
		];
	},
	addCommands() {
		return {
			setHeading:
				(attributes: { level: Level; className?: string }) =>
				// TODO: Need to research typing relation
				({ commands }: CommandProps) => {
					const attrs = this.options.levels.find(({ level }) => attributes.level === level);

					if (!attrs) {
						return false;
					}

					return commands.setNode(this.name, attrs);
				},
			toggleHeading:
				(attributes: { level: Level }) =>
				({ commands }: CommandProps) => {
					const attrs = this.options.levels.find(({ level }) => attributes.level === level);

					if (!attrs) {
						return false;
					}

					return commands.toggleNode(this.name, 'paragraph', attrs);
				},
		};
	},
	addKeyboardShortcuts() {
		return this.options.levels.reduce(
			(result, { level }) => ({
				...result,
				...{
					[`Mod-Alt-${level}`]: () => this.editor.commands.toggleHeading({ level }),
				},
			}),
			{}
		);
	},
	addInputRules() {
		return this.options.levels.map(({ level }) =>
			textblockTypeInputRule({
				find: new RegExp(`^(#{1,${level}})\\s$`),
				type: this.type,
				getAttributes: {
					level,
				},
			})
		);
	},
});
